import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { StateValues } from 'react-use-form-state';
import { AdminApi, CountryEnum, CurrencyEnum, AdminIssuerCreationRequest, DocumentsApi, DropdownElement } from 'api';
import useApiCall from 'hooks/use-api-call';
import Translate from 'ui/atoms/translate';
import { Modal, ModalHeader, ModalContent, ModalFooter, ModalFooterButtons, makeModalForm } from 'ui/molecules/modal';
import * as Styled from './styled';
import Header from 'ui/atoms/header';
import useCountries from 'hooks/use-countries';
import useCurrencies from 'hooks/use-currencies';
import { FileWithDocType } from 'ui/atoms/file-upload';
import Section from 'src/ui/atoms/section';
import Spacer from 'src/ui/atoms/spacer';
import Tooltip, { TooltipPosition } from 'src/ui/atoms/tooltip';
import Icon from 'src/ui/atoms/icon';
import useIsMedia, { DEVICES } from 'src/ui/hooks/use-is-media';
import { useCurrentUserSelector } from 'src/core/auth/hooks';
import { debounce } from 'lodash';

interface AddIssuerModalProps {
  onAddNewIssuer: () => void;
  onClose: () => void;
}

interface AddIssuerFields {
  organization: string;
  companyName: string;
  commercialRegister: string;
  commercialRegisterNumber: string;
  street: string;
  zip: string;
  city: string;
  country: CountryEnum;
  iban: string;
  accountHolder: string;
  currency: CurrencyEnum;
  bank: string;
  bic: string;
  bankCountry: CountryEnum;
  forename: string;
  surname: string;
  email: string;
  commercialRegisterStatement: FileWithDocType;
  commercialRegisterStatementId: string;
  listOfShareholders: FileWithDocType;
  listOfShareholdersId: string;
}

export const AddIssuerForm = makeModalForm<AddIssuerFields>();

const AddIssuerModal: FunctionComponent<AddIssuerModalProps> = ({ onAddNewIssuer, onClose }) => {
  const [organizations, setOrganizations] = useState<DropdownElement[]>([]);

  const { makeAuthenticatedApi, withApi, loading: apiLoading } = useApiCall();
  const { currentUser } = useCurrentUserSelector();
  const isPhone = useIsMedia(DEVICES.phone);
  const api: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);
  const documentsApi: DocumentsApi = useMemo(() => makeAuthenticatedApi(DocumentsApi), [makeAuthenticatedApi]);

  const onSubmit = useCallback(
    (fieldsValues: StateValues<AddIssuerFields>) => {
      withApi(async () => {
        const issuerCreationBody: AdminIssuerCreationRequest = {
          organizationId: fieldsValues.organization,
          naturalPerson: {
            forename: fieldsValues.forename,
            surname: fieldsValues.surname,
            email: fieldsValues.email,
          },
          legalPerson: {
            companyName: fieldsValues.companyName,
            street: fieldsValues.street,
            city: fieldsValues.city,
            zip: fieldsValues.zip,
            country: fieldsValues.country,
            commercialRegister: fieldsValues.commercialRegister,
            commercialRegisterNumber: fieldsValues.commercialRegisterNumber,
            commercialRegisterStatementId: fieldsValues.commercialRegisterStatementId,
            listOfShareholdersId: fieldsValues.listOfShareholdersId,
          },
          bankAccount: {
            accountHolder: fieldsValues.accountHolder,
            iban: fieldsValues.iban,
            bic: fieldsValues.bic,
            bank: fieldsValues.bank,
            country: fieldsValues.bankCountry,
            currency: fieldsValues.currency,
          },
        };

        if (fieldsValues.commercialRegisterStatement?.file) {
          const commercialRegisterStatementResponse = await documentsApi.adminDocumentsCreate({
            file: fieldsValues.commercialRegisterStatement?.file,
          });
          issuerCreationBody.legalPerson.commercialRegisterStatementId = commercialRegisterStatementResponse.id;
        }
        if (fieldsValues.listOfShareholders?.file) {
          const listOfShareholdersResponse = await documentsApi.adminDocumentsCreate({
            file: fieldsValues.listOfShareholders?.file,
          });
          issuerCreationBody.legalPerson.listOfShareholdersId = listOfShareholdersResponse.id;
        }

        await api.adminIssuersCreate({ adminIssuerCreationRequest: issuerCreationBody });
        onAddNewIssuer();
      });
    },
    [withApi, api],
  );

  const fetchOrganizations = useCallback(
    (searchQuery: string = '') => {
      withApi(async () => {
        const response = await api.adminIssuersDropdownOrganizationForIssuerCreationList({
          search: searchQuery,
          limit: 50,
        });
        setOrganizations(response.results || []);
      });
    },
    [withApi, api],
  );

  const searchOrganizationValue = useCallback(
    (value: string, action?: string) => {
      if (action === 'menu-close' && value === '') {
        return;
      }
      fetchOrganizations(value);
    },
    [fetchOrganizations],
  );

  const debouncedOrganizationSearch = useMemo(() => debounce(searchOrganizationValue, 500), [searchOrganizationValue]);

  const organizationOptions = useMemo(() => {
    return organizations.map((org) => ({
      value: org.id,
      label: org.name,
      name: org.name,
    }));
  }, [JSON.stringify(organizations)]);

  useEffect(() => {
    if (currentUser) {
      fetchOrganizations();
    }
  }, [currentUser, fetchOrganizations]);

  const countries = useCountries();
  const currencies = useCurrencies();

  if (!currentUser) return null;

  return (
    <Modal onClose={onClose}>
      <AddIssuerForm
        i18nKey="addIssuer"
        onSubmit={(values: StateValues<AddIssuerFields>) => {
          onSubmit(values);
        }}
        initial={{
          currency: 'EUR',
        }}
      >
        <ModalHeader>
          <Translate name="addIssuer.title" />
        </ModalHeader>
        <ModalContent>
          <AddIssuerForm.ValueProvider>
            {(values) => (
              <>
                <Section spacing="regular">
                  <Header spacing="medium">
                    <Translate name="addIssuer.subtitleOrganization" />
                  </Header>
                  <AddIssuerForm.Group
                    name="organization"
                    required={true}
                    requiredError={<Translate name="addIssuer.fields.organization.error" />}
                    tooltip={
                      <Tooltip
                        content={<Translate name="addIssuer.fields.organization.tooltip" />}
                        position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                      >
                        <Icon name="question" size="large" color="info" padded="none" />
                      </Tooltip>
                    }
                  >
                    <AddIssuerForm.Select
                      onInputChange={debouncedOrganizationSearch}
                      options={organizationOptions}
                    ></AddIssuerForm.Select>
                  </AddIssuerForm.Group>
                </Section>

                <Section spacing="regular">
                  <Header spacing="medium">
                    <Translate name="addIssuer.subtitleNameCommercialRegister" />
                  </Header>
                  <AddIssuerForm.Group name="companyName" required={true} occupyTooltipSpace>
                    <AddIssuerForm.Input />
                  </AddIssuerForm.Group>
                  <AddIssuerForm.Group name="commercialRegister" required={true} occupyTooltipSpace>
                    <AddIssuerForm.Input />
                  </AddIssuerForm.Group>
                  <AddIssuerForm.Group name="commercialRegisterNumber" required={true} occupyTooltipSpace>
                    <AddIssuerForm.Input />
                  </AddIssuerForm.Group>
                </Section>

                <Section spacing="regular">
                  <Header spacing="medium">
                    <Translate name="addIssuer.subtitleAddress" />
                  </Header>
                  <AddIssuerForm.Group name="street" required={true} occupyTooltipSpace>
                    <AddIssuerForm.Input />
                  </AddIssuerForm.Group>
                  <Styled.SameLine>
                    <AddIssuerForm.Group name="zip" required={true}>
                      <AddIssuerForm.Input />
                    </AddIssuerForm.Group>
                    <Spacer x={4} />
                    <AddIssuerForm.Group name="city" required={true} occupyTooltipSpace>
                      <AddIssuerForm.Input />
                    </AddIssuerForm.Group>
                  </Styled.SameLine>
                  <AddIssuerForm.Group name="country" required={true} occupyTooltipSpace>
                    <AddIssuerForm.Select options={countries} value={values.country}></AddIssuerForm.Select>
                  </AddIssuerForm.Group>
                </Section>

                <Section spacing="regular">
                  <Header spacing="medium">
                    <Translate name="addIssuer.subtitleBank" />
                  </Header>
                  <AddIssuerForm.Group name="iban" required={true} occupyTooltipSpace>
                    <AddIssuerForm.Input />
                    {AddIssuerForm.Validators.IBAN()}
                  </AddIssuerForm.Group>
                  <AddIssuerForm.Group name="accountHolder" required={true} occupyTooltipSpace>
                    <AddIssuerForm.Input />
                  </AddIssuerForm.Group>
                  <AddIssuerForm.Group name="currency" required={true} occupyTooltipSpace>
                    <AddIssuerForm.Select options={currencies} value={values.currency}></AddIssuerForm.Select>
                  </AddIssuerForm.Group>
                  <AddIssuerForm.Group name="bank" required={true} occupyTooltipSpace>
                    <AddIssuerForm.Input />
                  </AddIssuerForm.Group>
                  <AddIssuerForm.Group name="bic" required={true} occupyTooltipSpace>
                    <AddIssuerForm.Input />
                    {AddIssuerForm.Validators.Bic()}
                  </AddIssuerForm.Group>
                  <AddIssuerForm.Group name="bankCountry" required={true} occupyTooltipSpace>
                    <AddIssuerForm.Select options={countries} value={values.country}></AddIssuerForm.Select>
                  </AddIssuerForm.Group>
                </Section>

                <Section spacing="regular">
                  <Header spacing="medium">
                    <Translate name="addIssuer.subtitleCeo" />
                  </Header>
                  <Styled.SameLine>
                    <AddIssuerForm.Group name="forename" required={true}>
                      <AddIssuerForm.Input />
                    </AddIssuerForm.Group>
                    <Spacer x={4} />
                    <AddIssuerForm.Group name="surname" required={true} occupyTooltipSpace>
                      <AddIssuerForm.Input />
                    </AddIssuerForm.Group>
                  </Styled.SameLine>
                  <AddIssuerForm.Group name="email" required={true} occupyTooltipSpace>
                    <AddIssuerForm.Input />
                    {AddIssuerForm.Validators.Email()}
                  </AddIssuerForm.Group>
                </Section>

                <AddIssuerForm.Group name="commercialRegisterStatement" required={true}>
                  <AddIssuerForm.FileUpload />
                </AddIssuerForm.Group>
                <AddIssuerForm.Group name="listOfShareholders" required={true}>
                  <AddIssuerForm.FileUpload />
                </AddIssuerForm.Group>
              </>
            )}
          </AddIssuerForm.ValueProvider>
        </ModalContent>
        <ModalFooter>
          <ModalFooterButtons
            actionButtons={[
              {
                name: 'cancel',
                content: <Translate name="common.close" />,
                size: 'large',
                onClick: () => onClose(),
              },
              {
                name: 'send',
                content: <Translate name="common.add" />,
                variant: 'primary',
                type: 'submit',
                loading: apiLoading,
                size: 'large',
              },
            ]}
          />
        </ModalFooter>
      </AddIssuerForm>
    </Modal>
  );
};

export default AddIssuerModal;
